<template>
  <div>
    <SectionSeparatorWrapper headerText="Confirmation" headerRequiredFieldsText="indicates required field" :separator="false">
      <!-- Confirmation step with excess change -->
      <template v-if="isExcessChangeRequired">
        <div class="form-field">
          <p v-html="informationReferenceData.confirmationExcessChangeInformationText.informationText"></p>
        </div>

        <div class="form-field">
          <p class="h6">Current premium</p>
          <p>{{ formatAsCurrency(membershipDetails.premiumInformation.currentOngoingPremium) }} / {{ mapPaymentFrequencyFromRecurringToSingleton(membershipDetails.frequency) }}</p>
        </div>
        <div class="form-field">
          <p class="h6">Current excess</p>
          <p>{{ formatAsCurrency(membershipDetails.excess) }}</p>
        </div>
        <div class="form-field">
          <p class="h6">New composition</p>
          <p>{{ formattedNewComposition }}</p>
        </div>
        <FormField labelText="New nominated excess" :required="true" :helperText="informationReferenceData.confirmationExcessChangeHelperText.informationText">
          <DropdownSelect :items="excessDropdownOptions" :initialSelect="getInitialSelect" v-model="newExcess" />
        </FormField>
        <div class="form-field margin-bottom" v-if="newPremium">
          <p class="h6">New premium</p>
          <p>{{ formatAsCurrency(newPremium) }} / {{ mapPaymentFrequencyFromRecurringToSingleton(membershipDetails.frequency) }}</p>
        </div>
        <div class="form-field">
          <p v-html="informationReferenceData.confirmationExcessChangeSubmitText.informationText"></p>
        </div>
      </template>

      <!-- Confirmation step with no excess change -->
      <template v-else>
        <div>
          <p v-html="informationReferenceData.confirmationNoExcessChangeInformationText.informationText"></p>
        </div>

        <fieldset>
          <div class="form-field">
            <p class="h6">Current premium</p>
            <p>
              {{ formatAsCurrency(membershipDetails.premiumInformation.currentOngoingPremium) }} / {{ mapPaymentFrequencyFromRecurringToSingleton(membershipDetails.frequency) }}
            </p>
          </div>
          <!-- Hidden in UI for DIG-6738. Kept here in case it is required again in future -->
          <!-- <div class="form-field">
            <p class="h6">Current excess</p>
            <p>{{ formatAsCurrency(membershipDetails.excess) }}</p>
          </div> -->
          <div class="form-field" v-if="newPremium">
            <p class="h6">New premium</p>
            <p>{{ formatAsCurrency(newPremium) }} / {{ mapPaymentFrequencyFromRecurringToSingleton(membershipDetails.frequency) }}</p>
          </div>
        </fieldset>
      </template>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import SectionSeparatorWrapper from '@/components/SectionSeparatorWrapper.vue';
import { FormField } from '@/components';
import { DropdownSelect } from '@general/js/components';
import { formatAsCurrency, mapPaymentFrequencyFromRecurringToSingleton } from '@/utils';
import { mapCompositionToScaleDesc } from '@/utils/CRMUtils';
import AddRemovePersonMixins from './AddRemovePersonMixins';

export default {
  name: 'Confirmation',
  components: { SectionSeparatorWrapper, FormField, DropdownSelect },
  mixins: [AddRemovePersonMixins],

  props: {
    // Boolean to determine which version of the confirmation step to display
    isExcessChangeRequired: {
      type: Boolean,
      default: false,
    },
    // Array of excesses allowed for new composition, eg. [0,250,500]. This prop is required if isExcessChangeRequired is true.
    newExcessesList: {
      type: Array,
    },
    // New composition that will be applicable once the add/remove changes are submitted. This prop is required if isExcessChangeRequired is true.
    newComposition: {
      type: String,
    },
    // New premium to be displayed in confirmation step. This prop is required regardless of isExcessChangeRequired is true or false.
    newPremium: {
      type: Number,
      required: true,
    },
    informationReferenceData: {
      type: Object,
      required: true,
    },
    // This prop is used to re-set the newExcess value in data after the new premium api loading is complete, so that the newExcess value is not lost.
    propNewExcess: {
      type: String,
    },
  },

  data() {
    return {
      newExcess: null,
    };
  },

  mounted() {
    if (this.propNewExcess) {
      // Set the newExcess value to the propNewExcess value if it is available
      this.newExcess = this.propNewExcess;
    } else if (this.newExcessesList && this.newExcessesList.length > 0) {
      this.newExcess = this.newExcessesList[0]?.toString() ?? ''; // this assignment triggers the watcher below
    }
  },

  watch: {
    newExcess: {
      handler(val) {
        this.$emit('newExcessSelected', val);
      },
    },
  },

  computed: {
    excessDropdownOptions() {
      return this.newExcessesList.map((excess) => {
        return {
          value: String(excess),
          name: formatAsCurrency(excess),
        };
      });
    },

    getInitialSelect() {
      return this.excessDropdownOptions.find((option) => {
        return option.value === this.newExcess;
      })?.name;
    },

    formattedNewComposition() {
      // convert enum value to user friendly composition
      return mapCompositionToScaleDesc(this.newComposition);
    },
  },

  methods: {
    formatAsCurrency,
    mapPaymentFrequencyFromRecurringToSingleton,
  },
};
</script>

<style lang="scss" scoped>
.margin-bottom {
  margin-bottom: 2rem;
}
</style>
